/**
 * Mystery Shopping Client Dashboard — Aug 2026.
 * Rendered by /dashboard when the tenant is MS-only (has_mystery_shopping
 * without has_standard_surveys). Reuses the /reports filter bar and calls
 * /api/reports/dashboard-kpis + /api/reports for the visible data. Never
 * links to /responses.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { FiltersBar, presetRange } from '@/components/mystery_shopping/FiltersBar';
import { FileBarChart, ChevronRight } from 'lucide-react';
import { AskSatisFindCard, ManagementInsightsCard } from '@/pages/Dashboard';
import MSInsightsSection from '@/components/mystery_shopping/MSInsightsSection';
import useTenantFeatures from '@/hooks/useTenantFeatures';

function pct(v) { return (v == null || Number.isNaN(v)) ? '—' : `${v}%`; }
function fmtDate(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return s; }
}

function Tile({ label, value, sub, testid, onClick, expanded, children }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={
        'bg-white border border-[#E2E8F0] rounded-xl p-4 min-h-[92px] flex flex-col justify-between ' +
        (clickable ? 'cursor-pointer hover:border-[#94A3B8] hover:shadow-sm transition-colors ' : '') +
        (expanded ? 'ring-2 ring-[#1A3E6F]/30 ' : '')
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(e); } : undefined}
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#64748B]">{label}</p>
      <div className="mt-2">
        <p className="text-2xl font-bold leading-none text-[#0F172A]">{value}</p>
        {sub && <p className="text-[11px] text-[#94A3B8] mt-1">{sub}</p>}
      </div>
      {expanded && children ? (
        <div className="mt-3 pt-3 border-t border-[#E2E8F0]">{children}</div>
      ) : null}
    </div>
  );
}


function NpsRow({ label, value, colour, testid }) {
  return (
    <div className="flex items-center justify-between text-xs" data-testid={testid}>
      <span className="text-[#475569]">{label}</span>
      <span className="font-semibold" style={{ color: colour }}>{value == null ? '—' : `${value}%`}</span>
    </div>
  );
}


function NpsBreakdown({ nps, testidPrefix }) {
  // Feb 2026 refinement — NPS® breakdown shown when the card is expanded.
  // Values are already rounded server-side so P + Pa + D always totals 100.
  if (!nps || nps.base == null || nps.base === 0) {
    return (
      <p className="text-xs text-[#94A3B8]" data-testid={`${testidPrefix}-empty`}>
        No valid NPS® responses in the current filter.
      </p>
    );
  }
  return (
    <div className="space-y-1">
      <NpsRow label="Promoters (9–10)"  value={nps.promoters_pct}  colour="#059669" testid={`${testidPrefix}-promoters`} />
      <NpsRow label="Passives (7–8)"    value={nps.passives_pct}   colour="#B45309" testid={`${testidPrefix}-passives`} />
      <NpsRow label="Detractors (0–6)"  value={nps.detractors_pct} colour="#DC2626" testid={`${testidPrefix}-detractors`} />
      <p className="text-[11px] text-[#94A3B8] pt-1">n = {nps.base}</p>
    </div>
  );
}


function npsLabel(score) {
  if (score == null || Number.isNaN(Number(score))) return '—';
  const s = Number(score);
  return s > 0 ? `+${s}` : `${s}`;
}

export default function MysteryShoppingDashboard() {
  const navigate = useNavigate();
  const tenantFeatures = useTenantFeatures();
  // Step 2 (Feb 2026): default to All Time so the dashboard immediately
  // shows every published report available to the user. Preset chips remain
  // available for those who want to narrow the range.
  const [range, setRange] = useState(() => presetRange('all_time'));
  const [branchType, setBranchType] = useState('all');
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [branches, setBranches] = useState([]);
  const [kpis, setKpis] = useState(null);
  // Feb 2026 refinement — which NPS card (if any) is currently expanded
  // to show the promoters/passives/detractors breakdown.
  const [expandedNps, setExpandedNps] = useState(null);
  // Feb 2026 — Competitor Brand filter. '' means "All competitors".
  const [competitorBrand, setCompetitorBrand] = useState('');

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (range.from) p.set('date_from', range.from);
    if (range.to)   p.set('date_to', range.to);
    if (branchType && branchType !== 'all') p.set('branch_type', branchType);
    if (selectedBranchIds.length) p.set('branch_ids', selectedBranchIds.join(','));
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [range, branchType, selectedBranchIds]);

  // KPIs query string mirrors `queryString` but additionally attaches the
  // Competitor Brand filter. Kept separate so /reports (list) is never
  // affected by the brand filter — it only reshapes the Competitor
  // segment inside dashboard_kpis.
  const kpisQueryString = useMemo(() => {
    const p = new URLSearchParams();
    if (range.from) p.set('date_from', range.from);
    if (range.to)   p.set('date_to', range.to);
    if (branchType && branchType !== 'all') p.set('branch_type', branchType);
    if (selectedBranchIds.length) p.set('branch_ids', selectedBranchIds.join(','));
    if (competitorBrand) p.set('competitor_brand', competitorBrand);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [range, branchType, selectedBranchIds, competitorBrand]);

  // KPIs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/reports/dashboard-kpis${kpisQueryString}`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setKpis(d);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [kpisQueryString]);

  // Latest reports fetch removed — the "Latest published reports"
  // table on this dashboard was replaced by the Client Experience V2
  // Ask satisFIND + Management Insights modules, so we no longer
  // need to pull the reports list here.

  // Branch picker options (matches /reports)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = branchType && branchType !== 'all' ? `?branch_type=${branchType}` : '';
        const res = await fetch(`${API}/reports/branches${qs}`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setBranches(d.branches || []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [branchType]);

  // (Top-N most-recent reports removed with the Latest-reports table.)

  // Feb 2026 — dynamic list of Competitor Brands from the /reports/branches
  // picker. `''` (blank) is treated as "no brand configured" and does NOT
  // populate the dropdown. Selector is hidden when zero brands exist.
  const competitorBrands = useMemo(() => {
    const set = new Set();
    for (const b of (branches || [])) {
      if (b.branch_type !== 'competitor') continue;
      const brand = (b.competitor_brand || '').trim();
      if (brand) set.add(brand);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [branches]);

  // Reset the brand filter when the current selection disappears from the
  // list (e.g. tenant removed the last competitor branch with that brand).
  useEffect(() => {
    if (competitorBrand && !competitorBrands.includes(competitorBrand)) {
      setCompetitorBrand('');
    }
  }, [competitorBrands, competitorBrand]);

  // Step 2 (Feb 2026): Client and Competitor TrustX® must be rendered from
  // the independent per-classification segments so the two scores are
  // never merged. Comparison reports are counted but contribute to
  // neither score.
  const segClient     = kpis?.segments?.client     || {};
  const segCompetitor = kpis?.segments?.competitor || {};

  const classificationLabel = (cls) => {
    // Feb 2026 fix: read effective `classification` field from row.
    if (cls === 'client')     return { text: 'Client',     cls: 'bg-[#1A3E6F]/10 text-[#1A3E6F]' };
    if (cls === 'competitor') return { text: 'Competitor', cls: 'bg-[#D62B27]/10 text-[#D62B27]' };
    if (cls === 'comparison') return { text: 'Comparison', cls: 'bg-[#64748B]/10 text-[#64748B]' };
    return { text: 'Unclassified', cls: 'bg-[#FEF3C7] text-[#92400E]' };
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ms-dashboard">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Mystery Shopping Dashboard</h1>
          <p className="text-sm text-[#64748B] mt-0.5">Published FIND performance for the selected filters.</p>
        </div>

        <FiltersBar
          range={range}
          onRangeChange={setRange}
          branchType={branchType}
          onBranchTypeChange={setBranchType}
          selectedBranchIds={selectedBranchIds}
          onSelectedBranchIdsChange={setSelectedBranchIds}
          branches={branches}
          showExport={false}
        />

        {/* Client segment tiles */}
        <div>
          <p className="text-[11px] uppercase tracking-wider font-bold text-[#1A3E6F] mb-2" data-testid="ms-dash-segment-client-label">Client Reports</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile
              label="Client FINDs"
              value={segClient?.published ?? 0}
              sub={segClient?.branches_covered ? `${segClient.branches_covered} branch${segClient.branches_covered === 1 ? '' : 'es'} covered` : null}
              testid="ms-dash-tile-client-published"
            />
            <Tile
              label="TrustX®"
              value={pct(segClient?.trustx?.pct)}
              sub={segClient?.trustx?.n ? `n = ${segClient.trustx.n}` : null}
              testid="ms-dash-tile-client-trustx"
            />
            <Tile
              label="Brand Excellence"
              value={pct(segClient?.trustx_brand?.pct)}
              sub={segClient?.trustx_brand?.n ? `n = ${segClient.trustx_brand.n}` : null}
              testid="ms-dash-tile-client-brand"
            />
            <Tile
              label="Service Excellence"
              value={pct(segClient?.trustx_service?.pct)}
              sub={segClient?.trustx_service?.n ? `n = ${segClient.trustx_service.n}` : null}
              testid="ms-dash-tile-client-service"
            />
            <Tile
              label="Sales Excellence"
              value={pct(segClient?.trustx_sales?.pct)}
              sub={segClient?.trustx_sales?.n ? `n = ${segClient.trustx_sales.n}` : null}
              testid="ms-dash-tile-client-sales"
            />
            <Tile
              label="NPS®"
              value={npsLabel(segClient?.nps?.score)}
              sub={segClient?.nps?.base ? `n = ${segClient.nps.base}` : null}
              testid="ms-dash-tile-client-nps"
              expanded={expandedNps === 'client'}
              onClick={() => setExpandedNps(expandedNps === 'client' ? null : 'client')}
            >
              <NpsBreakdown nps={segClient?.nps} testidPrefix="ms-dash-client-nps" />
            </Tile>
          </div>
        </div>

        {/* Competitor segment tiles. */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider font-bold text-[#D62B27]" data-testid="ms-dash-segment-competitor-label">Competitor Reports</p>
            {competitorBrands.length > 0 && (
              <div className="flex items-center gap-2" data-testid="ms-dash-competitor-brand-filter">
                <label htmlFor="competitor-brand-select" className="text-[11px] text-[#64748B]">
                  Competitor Brand
                </label>
                <select
                  id="competitor-brand-select"
                  value={competitorBrand}
                  onChange={(e) => setCompetitorBrand(e.target.value)}
                  data-testid="ms-dash-competitor-brand-select"
                  className="h-8 rounded-md border border-[#E2E8F0] bg-white px-2 text-xs text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]/20"
                >
                  <option value="">All competitors</option>
                  {competitorBrands.map((brand) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile
              label="Competitor FINDs"
              value={segCompetitor?.published ?? 0}
              sub={segCompetitor?.branches_covered ? `${segCompetitor.branches_covered} branch${segCompetitor.branches_covered === 1 ? '' : 'es'} covered` : null}
              testid="ms-dash-tile-competitor-published"
            />
            <Tile
              label="TrustX®"
              value={pct(segCompetitor?.trustx?.pct)}
              sub={segCompetitor?.trustx?.n ? `n = ${segCompetitor.trustx.n}` : null}
              testid="ms-dash-tile-competitor-trustx"
            />
            <Tile
              label="Brand Excellence"
              value={pct(segCompetitor?.trustx_brand?.pct)}
              sub={segCompetitor?.trustx_brand?.n ? `n = ${segCompetitor.trustx_brand.n}` : null}
              testid="ms-dash-tile-competitor-brand"
            />
            <Tile
              label="Service Excellence"
              value={pct(segCompetitor?.trustx_service?.pct)}
              sub={segCompetitor?.trustx_service?.n ? `n = ${segCompetitor.trustx_service.n}` : null}
              testid="ms-dash-tile-competitor-service"
            />
            <Tile
              label="Sales Excellence"
              value={pct(segCompetitor?.trustx_sales?.pct)}
              sub={segCompetitor?.trustx_sales?.n ? `n = ${segCompetitor.trustx_sales.n}` : null}
              testid="ms-dash-tile-competitor-sales"
            />
            <Tile
              label="NPS®"
              value={npsLabel(segCompetitor?.nps?.score)}
              sub={segCompetitor?.nps?.base ? `n = ${segCompetitor.nps.base}` : null}
              testid="ms-dash-tile-competitor-nps"
              expanded={expandedNps === 'competitor'}
              onClick={() => setExpandedNps(expandedNps === 'competitor' ? null : 'competitor')}
            >
              <NpsBreakdown nps={segCompetitor?.nps} testidPrefix="ms-dash-competitor-nps" />
            </Tile>
          </div>
        </div>

        {/* Client Experience V2 — Ask satisFIND panel and Management
            Insights section replace the previous "Latest published reports"
            table. Both are already scoped to the tenant's authorized FINDs
            via the /api/reports/insights and /api/satisfind/ask endpoints.

            Jun 2026 — each is gated by its platform product feature toggle
            so a platform admin can disable MS Management Insights / Ask
            satisFIND without a redeploy. */}
        {tenantFeatures.product_features?.ask_satisfind && (
          <AskSatisFindCard dateParams={{ from: range.from, to: range.to }}
                            branchIds={selectedBranchIds.join(',') || undefined} />
        )}
        {/* Sept 2026 — Mystery Shopping UI refresh. Replaces the previous
            `<ManagementInsightsCard />` for MS-only tenants. NPS/CSAT/VOC/VOE
            surface is untouched — this dashboard is only ever rendered for
            has_mystery_shopping && !has_standard_surveys tenants. */}
        {tenantFeatures.product_features?.management_insights && (
          <MSInsightsSection dateParams={{ from: range.from, to: range.to }}
                             branchIds={selectedBranchIds.join(',') || undefined} />
        )}
      </div>
    </DashboardLayout>
  );
}
